import { useState, useEffect } from 'react';
import { Routes, Route, NavLink, useNavigate, Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, Users, Search, Bell, Settings, LogOut, MessageSquare, HelpCircle, Gem, ShieldCheck, Menu, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import DashboardOverview from './DashboardOverview';
import Pipeline from './Pipeline';
import SavedSearches from './SavedSearches';
import TalentBriefs from './TalentBriefs';
import Notifications from './Notifications';
import Account from './Account';
import Messages from './Messages';
import Support from './Support';
import DedicatedHiring from './DedicatedHiring';
import AdminPanel from './AdminPanel';
import { authFetch } from '../../utils/authFetch';
import { toast } from 'sonner';

const ADMIN_EMAILS = ['info@flence.com', 'admin@fashionspyder.com'];

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [stats, setStats] = useState({ total_pipeline: 0, by_stage: {}, saved_searches: 0, unread_notifications: 0 });
  const [dedicatedBadge, setDedicatedBadge] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isAdmin = ADMIN_EMAILS.includes(user?.email?.toLowerCase());

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Close sidebar on outside click / escape key (mobile)
  useEffect(() => {
    if (!sidebarOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setSidebarOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [sidebarOpen]);

  useEffect(() => {
    const loadStats = () => {
      authFetch(`${BACKEND}/api/dashboard/stats`, {})
        .then(r => r.json())
        .then(d => { if (d.success) setStats(d.stats); })
        .catch(() => {});
    };
    loadStats();
    window.addEventListener('dashboard-stats-refresh', loadStats);
    return () => window.removeEventListener('dashboard-stats-refresh', loadStats);
  }, []);

  // Dedicated Hiring badge (quote pending + unread scout messages) — ungated
  useEffect(() => {
    const loadBadge = () => {
      authFetch(`${BACKEND}/api/dedicated/badge`, {})
        .then(r => r.json())
        .then(d => { if (d.success) setDedicatedBadge(d.count || 0); })
        .catch(() => {});
    };
    loadBadge();
    window.addEventListener('dedicated-badge-refresh', loadBadge);
    const iv = setInterval(loadBadge, 60000);
    return () => { window.removeEventListener('dedicated-badge-refresh', loadBadge); clearInterval(iv); };
  }, []);

  // Global subscription gate handler — redirects to pricing when API returns 403 subscription_required
  useEffect(() => {
    const handler = (e) => {
      const { required_plan, expired } = e.detail || {};
      if (expired) {
        toast.error('Your subscription has expired. Renew to restore access.', {
          action: { label: 'Renew', onClick: () => navigate('/dashboard/account') },
          duration: 8000,
        });
      } else {
        const planLabel = required_plan === 'intelligence' ? 'INTELLIGENCE' : 'RECON';
        toast.error(`${planLabel} subscription required for this feature.`, {
          action: { label: 'Upgrade', onClick: () => navigate('/pricing') },
          duration: 6000,
        });
      }
    };
    window.addEventListener('fs_subscription_required', handler);
    return () => window.removeEventListener('fs_subscription_required', handler);
  }, [navigate]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const planBadge = {
    free: { label: 'Free', cls: 'bg-secondary text-muted-foreground' },
    recon: { label: 'RECON', cls: 'bg-blue-100 text-blue-800' },
    intelligence: { label: 'INTELLIGENCE', cls: 'bg-purple-100 text-purple-800' },
  }[user?.subscription_plan || 'free'];

  const navItems = [
    { to: '/dashboard', end: true, icon: <LayoutDashboard className="w-4 h-4" />, label: 'Overview' },
    { to: '/dashboard/pipeline', icon: <Users className="w-4 h-4" />, label: 'Pipeline', badge: stats.total_pipeline || null },
    { to: '/dashboard/briefs', icon: <Search className="w-4 h-4" />, label: 'Talent Briefs', badge: null },
    { to: '/dashboard/messages', icon: <MessageSquare className="w-4 h-4" />, label: 'Outreach' },
    { to: '/dashboard/notifications', icon: <Bell className="w-4 h-4" />, label: 'Notifications', badge: stats.unread_notifications || null },
    { to: '/dashboard/account', icon: <Settings className="w-4 h-4" />, label: 'Account' },
    { to: '/dashboard/dedicated', icon: <Gem className="w-4 h-4" />, label: 'Dedicated Hiring', gold: true, badge: dedicatedBadge || null, flash: dedicatedBadge > 0 },
    { to: '/dashboard/support', icon: <HelpCircle className="w-4 h-4" />, label: 'Support' },
    ...(isAdmin ? [{ to: '/dashboard/admin', icon: <ShieldCheck className="w-4 h-4" />, label: 'Admin Panel', admin: true }] : []),
  ];

  return (
    <div className="min-h-screen bg-[#f9fafb]">
      {/* Mobile overlay backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 md:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`w-72 md:w-60 bg-white border-r border-border flex flex-col fixed h-full z-40 transition-transform duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}
        data-testid="dashboard-sidebar"
      >
        {/* Sidebar header */}
        <div className="p-5 border-b border-border flex items-center justify-between">
          <div>
            <Link to="/" className="text-base font-bold tracking-tight">FashionSpyder</Link>
            <p className="text-xs text-muted-foreground mt-0.5">HR Dashboard</p>
          </div>
          <button
            className="md:hidden p-1 text-muted-foreground hover:text-foreground"
            onClick={() => setSidebarOpen(false)}
            data-testid="sidebar-close-btn"
            aria-label="Close menu"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          {navItems.map(item => (
            <NavLink key={item.to} to={item.to} end={item.end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-3 md:py-2 text-sm transition-colors ${
                  isActive ? 'bg-foreground text-background' : item.admin ? 'text-amber-700 hover:bg-amber-50' : item.gold ? 'text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary)/0.08)]' : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
                }`
              }>
              {item.icon}
              <span className="flex-1">{item.label}</span>
              {item.badge > 0 && (
                <span className={`text-xs px-1.5 py-0.5 min-w-[20px] text-center ${item.flash ? 'bg-[hsl(var(--primary))] text-white animate-pulse' : 'bg-foreground text-background'}`}>
                  {item.flash ? '+' : ''}{item.badge}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-border">
          <div className="flex items-center gap-3 mb-3">
            {user?.picture_url ? (
              <img src={user.picture_url} alt={user.full_name} className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
            ) : (
              <div className="w-9 h-9 bg-foreground text-background flex items-center justify-center text-xs font-bold rounded-full flex-shrink-0">
                {(user?.full_name || 'U')[0].toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold truncate">{user?.full_name}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.company_name}</p>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <span className={`text-xs px-2 py-0.5 font-medium ${planBadge.cls}`}>{planBadge.label}</span>
            <button onClick={handleLogout} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-testid="logout-btn">
              <LogOut className="w-3 h-3" /> Logout
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-20 bg-white border-b border-border flex items-center justify-between px-4 h-14">
        <button
          onClick={() => setSidebarOpen(true)}
          className="p-2 -ml-2 text-foreground"
          data-testid="sidebar-open-btn"
          aria-label="Open menu"
        >
          <Menu className="w-5 h-5" />
        </button>
        <Link to="/" className="text-sm font-bold tracking-tight">FashionSpyder</Link>
        <div className="flex items-center gap-2">
          {stats.unread_notifications > 0 && (
            <Link to="/dashboard/notifications" className="relative p-2">
              <Bell className="w-4 h-4 text-muted-foreground" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
            </Link>
          )}
          <div className="w-7 h-7 bg-foreground text-background flex items-center justify-center text-xs font-bold rounded-full">
            {(user?.full_name || 'U')[0].toUpperCase()}
          </div>
        </div>
      </div>

      {/* Main content */}
      <main className="md:ml-60 flex-1 p-4 md:p-8 pt-[72px] md:pt-8">
        <Routes>
          <Route index element={<DashboardOverview stats={stats} />} />
          <Route path="pipeline" element={<Pipeline />} />
          <Route path="pipeline/:candidateId" element={<Pipeline />} />
          <Route path="searches" element={<SavedSearches />} />
          <Route path="briefs" element={<TalentBriefs />} />
          <Route path="messages" element={<Messages />} />
          <Route path="notifications" element={<Notifications />} />
          <Route path="account" element={<Account />} />
          <Route path="dedicated" element={<DedicatedHiring />} />
          <Route path="support" element={<Support />} />
          {isAdmin && <Route path="admin" element={<AdminPanel />} />}
        </Routes>
      </main>
    </div>
  );
};

export default Dashboard;
